import { useState } from 'react';
import { UserPlus, AlertCircle } from 'lucide-react';
import { User, UserRequest } from '../types';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { generateId } from '../utils/idGenerator';

interface UserRequestModalProps {
  users: User[];
  currentUserId: string;
  currentUserName: string;
  onClose: () => void;
  onSubmit: (request: UserRequest) => void;
  entityType: 'task' | 'epic';
}

export default function UserRequestModal({
  users,
  currentUserId,
  currentUserName,
  onClose,
  onSubmit,
  entityType,
}: UserRequestModalProps) {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [requestType, setRequestType] = useState<UserRequest['requestType']>('comment');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState<UserRequest['priority']>('medium');

  const availableUsers = (users || []).filter(u => u.id !== currentUserId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedUserId) {
      alert('Please select a user');
      return;
    }

    const selectedUser = (users || []).find(u => u.id === selectedUserId);
    if (!selectedUser) return;

    const request: UserRequest = {
      id: generateId('request', entityType, selectedUserId),
      requestedBy: currentUserId,
      requestedByName: currentUserName,
      requestedUser: selectedUserId,
      requestedUserName: selectedUser.name,
      requestType,
      message: message.trim() || undefined,
      status: 'pending',
      createdAt: new Date().toISOString(),
      priority,
    };

    onSubmit(request);
    onClose();
  };

  const requestTypeDescriptions: Record<UserRequest['requestType'], string> = {
    comment: 'Ask for a comment or feedback',
    planning_poker: 'Request estimation via planning poker',
    review: 'Ask for code or work review',
    approval: 'Request approval or sign-off',
    input: 'Ask for general input or advice',
    estimate: 'Request time estimation',
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <UserPlus className="w-5 h-5 text-primary" />
            Request User Input
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Info Banner */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm text-primary">
              <p className="font-medium mb-1">How it works:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>The user will be notified and this {entityType} will appear in their dashboard</li>
                <li>You'll get a notification when they respond</li>
                <li>Reminders are sent until the request is fulfilled</li>
              </ul>
            </div>
          </div>

          {/* Select User */}
          <div className="space-y-2">
            <Label htmlFor="user-request-user-select">
              Select User <span className="text-destructive">*</span>
            </Label>
            <select
              id="user-request-user-select"
                            title="Select team member"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              required
            >
              <option value="">Choose a team member...</option>
              {availableUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email}) - {user.role}
                </option>
              ))}
            </select>
          </div>

          {/* Request Type */}
          <div className="space-y-2">
            <Label>
              Request Type <span className="text-destructive">*</span>
            </Label>
            <div className="space-y-2">
              {(Object.keys(requestTypeDescriptions) as UserRequest['requestType'][]).map(type => (
                <label
                  key={type}
                  className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    requestType === type
                      ? 'border-primary/40 bg-primary/10'
                      : 'border-border hover:border-accent'
                  }`}
                >
                  <input
                    type="radio"
                    name="requestType"
                    value={type}
                    checked={requestType === type}
                    onChange={(e) => setRequestType(e.target.value as UserRequest['requestType'])}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium capitalize">{type.replace('_', ' ')}</div>
                    <div className="text-sm text-muted-foreground">{requestTypeDescriptions[type]}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label htmlFor="user-request-priority">Priority</Label>
            <select
              id="user-request-priority"
                            title="Select priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as UserRequest['priority'])}
              className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="low">Low - Can wait</option>
              <option value="medium">Medium - Normal priority</option>
              <option value="high">High - Important</option>
              <option value="urgent">Urgent - Needs immediate attention</option>
            </select>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label>Message (Optional)</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add context or specific questions for the user..."
              rows={4}
              className="resize-none"
            />
            <p className="text-sm text-muted-foreground">
              Explain what you need from this user and any relevant context
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              <UserPlus className="w-4 h-4 mr-2" />
              Send Request
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}